import _ from 'lodash';
import PropTypes from 'prop-types';
import React, {useCallback, useContext, useEffect, useRef, useState, cloneElement} from 'react';
import localforage from 'localforage';
import {arrayMoveImmutable} from 'array-move';

import {storageVersion, TableColumn, TableOrderBy, TableRowRenderer, TableSettings} from '../tableSettings';
import PrimaryButton from '../ui/PrimaryButton';
import PrimaryInverseButton from '../ui/PrimaryInverseButton';
import DarkInverseButton from '../ui/DarkInverseButton';
import CheckBoxIcon from '../ui/CheckBoxIcon';
import Table, { PagingModel } from '../ui/tables/Table';
import ApplicationContext from '../ApplicationContext';
import useGlobalOkDialog from '../hooks/useGlobalOkDialog';
import IfmsModal from '../ui/IfmsModal';
import SecondaryButton from '../ui/SecondaryButton';
import BlackInverseButton from '../ui/BlackInverseButton';
import { ActionButtonItem } from '../ui/ActionButton';
import SearchByIcon2 from '../img/Search2.svg';
import SearchByDarkIcon2 from '../img/Search_Dark2.svg';
import SearchColumn from './SearchColumn';
import { TextRenderer } from './SmartTableRenderers';
import useTableNavigation from '../ui/tables/useTableNavigation';
import useFastStorage from '../hooks/useFastStorage';
import { sortingListData } from '../components/referenceFiles/ReferenceFiles';
import * as XCol from '../framework.core/XExportColumn';
import TextBox from '../ui/TextBox';

function createDefaultSettings(
  overflowX:string|undefined,
  overflowY:string|undefined,
  columns:Array<TableColumn>,
  containerWidth:number,
) {
  const result:TableSettings = {
    overflowX,
    overflowY,
    columns: [],
  };
  
  /**
   * sum of column width from tableSettings
   */
  const calcFixedWidth = columns.filter(x => x.width).reduce((sum, column) => sum + (column.width ?? 0), 0);
  let totalWidth = 0;
  let width = 50;

  for (let i = 0; i < columns.length; ++i) {
    const column = columns[i];

    // width가 지정되었으면 그대로 사용.
    if (column.width) {
      totalWidth += column.width;
      result.columns.push({ ...column });
      continue;
    }

    // column.width = undefined or 0 or null (지정되어 있지 않으면)
    //  initialWidthPercentage를 containerWidth에 배율로 지정하고,
    if (column.initialWidthPercentage && containerWidth > 0) {
      width = (column.initialWidthPercentage / 100) * (containerWidth - calcFixedWidth);
    }

    // Height
    if (overflowY !== 'hidden') {
      // vertical scrollbar width를 감안하여 마지막 앞, 마지막 column의 width 조정.
      if (i === columns.length - 2) {
        width = containerWidth - totalWidth - 18;
      }
      else if (i === columns.length - 1) { // Scroll size = 18
        width = 18;
      }
    }
    else {
      // overflowY가 hidden으로 지정되었으면 마지막 column에 나머지 width 적용
      if (i === columns.length - 1)
        width = containerWidth - totalWidth;
    }
    totalWidth += width;

    result.columns.push({
      ...column,
      width,
    })
  }

  return result;
}

function ColumnNameRenderer({row}:any) {
  return <div className="col-settings-col-name">{row.original.columnName}</div>;
}

function VisibleRenderer({row, data}:any) {
  const toggleColumn = data[0].toggleColumn;

  return (
    <div className="col-settings-col-visible" style={{width: '100%',}}>
      <DarkInverseButton size='regular' id={`column-option-col-visible-${row.original.accessor}`} onClick={() => toggleColumn(row.original.accessor)} style={{height: '20px'}}>
        <CheckBoxIcon falseColor='red' value={row.original.visible} />
      </DarkInverseButton>
    </div>
  );
}

interface SettingsDataElement {
  accessor: string,
  columnName: any,
  visible: boolean,
  canBeToggled: boolean,
  hiddenInColumnSetup: boolean,
};

// Implementation note:
//   New requirement is to remove columns that cannot be toggled from the list completely.
// In case that is changed again, remove x.canBeToggled condition.
export function ColumnSettings({
  originalColumns,
  currentColumns,
  onCancel,
  onSave,
  isLock,
}: {
  originalColumns: Array<TableColumn>,
  currentColumns: Array<TableColumn>,
  onCancel: VoidFunction,
  onSave: (c:Array<TableColumn>) => void,
  isLock?: boolean,
}) {
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const appContext = useContext(ApplicationContext);

  const [isHoverVisibleButton, setIsHoverVisibleButton] = useState(false);
  const [isHoverSettingButton, setIsHoverSettingButton] = useState(false);

  const initialData = currentColumns.map(x => ({
    accessor: x.accessor,
    columnName: x.Header,
    visible: findIsCurrentlyVisible(x.accessor),
    canBeToggled: !!x.canBeHidden,
    hiddenInColumnSetup: !!x.hiddenInColumnSetup,
  }));

  const [data, _setData] = useState<Array<SettingsDataElement>>(initialData);
  const dataRef = useRef(initialData);

  function setData(x: Array<SettingsDataElement>) {
    dataRef.current = x;
    _setData(x);
  }

  const [selectedRowIndex, setSelectedRowIndex] = useState(getActualIndexOfFirstVisibleEntryInTable());  // Actual row index: note some data might be hidden in column setup
  const [selectedRowIndexInTable, setSelectedRowIndexInTable] = useState(0);  // Selected row index in table
  const { selectTableRow, tableNavigationCleanup } = useTableNavigation('ifms-table-settings', onRowIndexChanged, onRowSelect, [], false, true, false);
  const [selectedPopupMenuItem, setSelectedPopupMenuItem] = useState<ActionButtonItem|null>(null);
  const [searchFilter, setSearchFilter] = useState('');
  const filterRef = useRef(searchFilter);
  
  const onPopupClose = () => {
    setSelectedPopupMenuItem(null);
  }

  const handleColumnSearch = (searchResults: any[]) => {
    if (searchResults.length > 0) {
      const matchedIndices = displayedData.map((item, index) => searchResults.some(x => x.columnName === item.columnName) ? index : -1).filter(idx => idx !== -1);
      
      if (matchedIndices.length > 0) {
        selectTableRow(matchedIndices[0]);
        onRowSelect(matchedIndices[0]);
        scrollSelectedTableRowIntoView();
      }
    }
  }

  const [orderBy, setOrderBy] = useFastStorage(`columnSettings-orderby`, {
    orderBy: '',
    orderByAscending: true,
  });
  const [isSorting, setIsSorting] = useState(false);
  
  useEffect(() => {
    selectTableRow(0);

    return () => {
      tableNavigationCleanup();
    };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
      filterRef.current = searchFilter;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchFilter]
  );

  function onEnter() {
    const currentFilter = filterRef.current.trim().toLowerCase();

    if (currentFilter.length > 0) {
      const columns = getDisplayData();
      const matchedIndices = columns.map((item, index) => (item.columnName || '').toString().toLowerCase().startsWith(currentFilter) ? index : -1).filter(idx => idx !== -1);
      
      if (matchedIndices.length > 0) {
        selectTableRow(matchedIndices[0]);
        onRowSelect(matchedIndices[0]);
        scrollSelectedTableRowIntoView();
      }
    }
  }

  function findIsCurrentlyVisible(accessor:string) {
    const match = currentColumns.find(x => x.accessor === accessor);
    if (match) {
      return match.visible;
    }

    return false;
  }

  function getActualIndexOfFirstVisibleEntryInTable() {
    const dd = getDisplayData();
    if (dd.length <= 0) {
      return -1;
    }

    const accessor = dd[0].accessor;

    return data.findIndex(x => x.accessor === accessor);
  }

  function getDisplayData() {
    let displayedData =  dataRef.current.filter(x => !x.hiddenInColumnSetup && x.canBeToggled);
    let enableBranch = (appContext?.systemSettings?.enableBranch ?? false);
    let useVat = (appContext?.systemSettings?.useVat ?? false);
    let enableIpRestr = (appContext?.systemSettings?.enableIpRestr ?? false);

    if (!enableBranch) {
      displayedData = displayedData.filter(x => x.accessor !== 'br' && x.accessor !== 'branch');
    }
    if (!useVat) {
      displayedData = displayedData.filter(x => x.accessor !== 'vatAmount');
    }
    if (!enableIpRestr) {
      displayedData = displayedData.filter(x => x.accessor !== 'limitLoginIp');
    }

    return displayedData;
  }

  function applyVisibilityToAll() {
    const visibleColumns = getDisplayData();
    
    const newCurrentState = [...data];

    visibleColumns.forEach(x => {
      let updateIndex = newCurrentState.findIndex(l => l.accessor === x.accessor);

      if (updateIndex >= 0 && newCurrentState[updateIndex].canBeToggled) {
        newCurrentState[updateIndex].visible = true;
      } 
    });

    setData(newCurrentState);
  }

  function doProcessRowSelection(rowIndexInTable: number) {
    if(rowIndexInTable === -1){
      setSelectedRowIndex(-1);
      setSelectedRowIndexInTable(-1);
      return;
    }
    const accessor = getDisplayData()[rowIndexInTable].accessor;

    setSelectedRowIndex(dataRef.current.findIndex(x => x.accessor === accessor));

    setSelectedRowIndexInTable(rowIndexInTable);
  }

  function onRowIndexChanged(rowIndex: number) {
    doProcessRowSelection(rowIndex);
  }

  function onRowSelect(rowIndex: number) {
    doProcessRowSelection(rowIndex);
  }

  function toggleColumn(accessor:string) {
    const updateIndex = data.findIndex(x => x.accessor === accessor );
    if (updateIndex >= 0) {
      const newCurrentState = [...data];
      newCurrentState[updateIndex].visible = !newCurrentState[updateIndex].visible;
      setData(newCurrentState);
    }
  }

  function scrollSelectedTableRowIntoView() {
    const processSelection = () => {
      const table = document.getElementById('ifms-table-settings');
      if (!table) {
        return;
      }

      const selectedRows = table.getElementsByClassName('data-row-selected');
      if (selectedRows.length > 0) {
        selectedRows[0].scrollIntoView({behavior: 'smooth', block: 'end'});
      }
    };

    setTimeout(processSelection, 10);
  }

  function moveFirst() {
    //const includeDID = data.find(x => x.columnName === 'D/ID') ? 1 : 0;
    //const newIndex = includeDID + data.filter(x => x.hiddenInColumnSetup).length;
    const newIndex = data.filter(x => x.hiddenInColumnSetup && x.visible).length ?? 0;

    const newData = arrayMoveImmutable(data, selectedRowIndex, newIndex);
    setData(newData);

    setSelectedRowIndex(newIndex);

    selectTableRow(0, true);
    setSelectedRowIndexInTable(0);

    scrollSelectedTableRowIntoView();
  }

  function moveLast() {
    const newData = arrayMoveImmutable(data, selectedRowIndex, data.length - 1);
    setData(newData);

    setSelectedRowIndexInTable(getDisplayData().length - 1);
    setSelectedRowIndex(data.length - 1);

    selectTableRow(getDisplayData().length - 1, true);
    scrollSelectedTableRowIntoView();
  }

  function moveUp() {
    if (selectedRowIndex < 0) {
      return;
    }

    let newIndex = selectedRowIndex - 1;
    if (newIndex < 0) {
      return;
    }

      while (data[newIndex].hiddenInColumnSetup || (!appContext.suntekSettings?.company?.enableBranch && data[newIndex].columnName === 'BR')) {
      --newIndex;
      if (newIndex < 0) {
        return;
      }
    }

    const newData = arrayMoveImmutable(data, selectedRowIndex, newIndex);
    setData(newData);

    setSelectedRowIndexInTable(selectedRowIndexInTable - 1);
    setSelectedRowIndex(newIndex);

    selectTableRow(selectedRowIndexInTable - 1, true);
    scrollSelectedTableRowIntoView();
  }

  function moveDown() {
    if (selectedRowIndex < 0) {
      return;
    }

    let newIndex = selectedRowIndex + 1;
    if (newIndex >= data.length) {
      return;
    }

    while (data[newIndex].hiddenInColumnSetup || (!appContext.suntekSettings?.company?.enableBranch && data[newIndex].columnName === 'BR')) {
      ++newIndex;
      if (newIndex >= data.length) {
        return;
      }
    }

    const newData = arrayMoveImmutable(data, selectedRowIndex, newIndex);
    setData(newData);

    setSelectedRowIndexInTable(selectedRowIndexInTable + 1);
    setSelectedRowIndex(newIndex);

    selectTableRow(selectedRowIndexInTable + 1, true);
    scrollSelectedTableRowIntoView();
  }

  function reorder() {
    //setRequireReorder(true);

    // const findCurrentVisibilitySetting = (accessor: string, defaultValue: boolean): boolean => {
    //   const el = data.find(x => x.accessor === accessor);
    //   if (el) {
    //     return el.visible;
    //   }

    //   return defaultValue;
    // };

    const initialData = originalColumns.map(x => ({
      accessor: x.accessor,
      columnName: x.Header,
      // visible: findCurrentVisibilitySetting(x.accessor, findIsCurrentlyVisible(x.accessor)),
      visible: x.visible,
      canBeToggled: !!x.canBeHidden,
      hiddenInColumnSetup: !!x.hiddenInColumnSetup,
    }));

    setData(initialData);

    setSelectedRowIndex(getActualIndexOfFirstVisibleEntryInTable());
    setSelectedRowIndexInTable(0);
    selectTableRow(0, true);
  }

  async function processSave() {
    if (data.every(x => !x.visible || x.hiddenInColumnSetup)) {
      await showGlobalOkDialog('Cannot save column option without any selection');
      return;
    }

    let newColumns:Array<TableColumn> = [];

    //<pt250426: No more use>
    // function findVisibility(accessor:string) {
    //   const match = data.find(x => x.accessor === accessor);
    //   if (match) {
    //     return match.visible;
    //   }
    //   return false;
    // }
    //pt250426>

    // if (requireReorder) {
    //   originalColumns.forEach(o => {
    //     const c = currentColumns.find(x => x.accessor === o.accessor);
    //     if (!c) {
    //       return;
    //     }

    //     newColumns.push({
    //       ...c,
    //       visible: findVisibility(c.accessor)
    //     });
    //   });
    // } else {
    //   data.forEach(d => {
    //     const c = currentColumns.find(x => x.accessor === d.accessor);
    //     if (!c) {
    //       // Should never happen
    //       return;
    //     }

    //     newColumns.push({
    //       ...c,
    //       visible: findVisibility(c.accessor),
    //     });
    //   });
    // }

    // data contains SettingsData to save, currentColumns contains initial SettingsData.
    let colChanged = false;
    let seqChanged = data.some((d, i) => d.accessor !== currentColumns[i].accessor);
    data.forEach(d => {
      const c = currentColumns.find(x => x.accessor === d.accessor);
      if (!c) {
        // Should never happen
        return;
      }
      //debugger;
      newColumns.push({
        ...c,
        visible: d.visible, //<pt250426>  findVisibility(c.accessor),
      });
      if (c.visible !== d.visible)
        colChanged = true;
    });

    //<pt250426: call save only when there is any change>
    if (seqChanged || colChanged)
      onSave(newColumns);
  }

  const displayColumns = [
    {
      Header: '',
      accessor: 'indicator',
      resizable: false,
      sortable: false,
      canBeHidden: false,
      visible: true,
      Cell: TextRenderer,
      width: 30,
    },
    {
      Header: 'Column Name',
      accessor: 'columnName',
      resizable: false,
      sortable: false,
      canBeHidden: false,
      visible: true,
      Cell: ColumnNameRenderer,
      width: 180,
    },
    {
      Header: 'Visible',
      accessor: 'visible',
      resizable: false,
      sortable: false,
      canBeHidden: false,
      visible: true,
      Cell: VisibleRenderer,
      width: 70,
    },
    {
      Header: '',
      accessor: 'scroll',
      resizable: false,
      sortable: false,
      canBeHidden: false,
      visible: true,
      width: 20,
    },
  ];

  const displayedData = getDisplayData();
  
  //const canNotMoveColumn = displayedData[0].columnName === 'D/ID' && selectedRowIndex === 2;
  // const canNotClickUp = selectedRowIndexInTable === 0;
  // const canNotClickDown = selectedRowIndexInTable === displayedData.length - 1;

  function onHeaderClick(header:any) {
    if (header && header.id !== 'indicator') {
      if (header.id === orderBy.orderBy) {
        setOrderBy({orderByAscending: !orderBy.orderByAscending});
      } else {
        setOrderBy({orderBy: header.id, orderByAscending: true});
      }
      setIsSorting(true);
      sortingListData(ColSetting, displayedData, setData, orderBy);
    }
  }

  return (
    <div className='report-popup'>
      <div style={{display: 'flex'}}>
        <div style={{width: '120px'}}>
          <PrimaryInverseButton name='column-option-all-visible' style={{ backgroundColor: isHoverVisibleButton ? '#B3C9DF' : 'transparent ', padding: '2px 10px', fontSize: '11px' }} size="regular" onClick={applyVisibilityToAll} onMouseEnter={() => setIsHoverVisibleButton(true)} onMouseLeave={() => setIsHoverVisibleButton(false)}>
            Select all visible
          </PrimaryInverseButton>&nbsp;
        </div>

        <div>
          <PrimaryInverseButton name='column-option-set-default-order' style={{ backgroundColor: isHoverSettingButton ? '#B3C9DF' : 'transparent ', padding: '2px 10px', fontSize: '11px' }}  
          size="regular" onClick={() => {reorder(); setIsSorting(false); setOrderBy({orderBy: '', orderByAscending: true});}} onMouseEnter={() => setIsHoverSettingButton(true)} onMouseLeave={() => setIsHoverSettingButton(false)}>
            Set default setting
          </PrimaryInverseButton>
        </div>

        <br />
      </div>

      <div style={{ marginTop: '10px', marginLeft: '4px', width: '230px' }}>
        <TextBox textId='column-option-filter' placeholder="Search" value={searchFilter} onChange={e => setSearchFilter(e.target.value)} onEnter={onEnter} />
      </div>

      <div style={{display: 'flex', marginTop: '10px', marginBottom: '10px'}}>
        <PrimaryButton id='column-option-up-button' disabled={selectedRowIndex < 0 || isLock} onClick={moveUp} size="small" style={{marginRight: '10px', fontSize: '11px'}}>Up</PrimaryButton>
        <PrimaryButton id='column-option-down-button' disabled={selectedRowIndex < 0 || isLock} onClick={moveDown} size="small" style={{marginRight: '10px', fontSize: '11px'}}>Down</PrimaryButton>
        <SecondaryButton id='column-option-top-button' disabled={selectedRowIndex < 0 || isLock} onClick={moveFirst} size="small" style={{marginRight: '10px', fontSize: '11px'}}>Top</SecondaryButton>
        <SecondaryButton id='column-option-bottom-button' disabled={selectedRowIndex < 0 || isLock} onClick={moveLast} size="small" style={{fontSize: '11px'}}>Bottom</SecondaryButton>
        {/* <BlackInverseButton name='column-option-search-button' size="small" onClick={() => setSelectedPopupMenuItem({ label: 'Search - Column Name', popupComponent: <SearchColumn displayedData={displayedData} onPopupClose={onPopupClose}/> })}>
          <img width='20px' height='20px' src={appContext.useDarkMode ? SearchByDarkIcon2 : SearchByIcon2} alt=""/>
        </BlackInverseButton> */}
      </div>
      <div style={{ fontSize: '11px', fontWeight: 'bold' }}>
        Total Number of Line: {displayedData.length}
      </div>
      {!!selectedPopupMenuItem && !!selectedPopupMenuItem.popupComponent ?
        <IfmsModal
          isSmallHeader
          isBlueHeader
          cancelOnEsc
          onCancel={onPopupClose}
          draggable
          isOpen={!!selectedPopupMenuItem}
          title={"Search - Column Name"}
          width={"400px"}
          top="250px"
          left="1170px">
            {cloneElement(selectedPopupMenuItem.popupComponent!, { onSearchResult: handleColumnSearch, onPopupClose })}
        </IfmsModal>
      : null }

      <div style={{width: '300px'}}>
        <Table
          customTableId="ifms-table-settings"
          columns={displayColumns}
          data={displayedData.map((x, i) => ({...x, indicator: i + 1, toggleColumn }))}
          height="500px"
          overflowX="hidden"
          overflowY="scroll"
          headersColor="white"
          selectable
          orderBy={[orderBy]}
          useHighlightCellOnMouseover={false}
          useSetClickedCellWhenClick={false}
          onHeaderClick={onHeaderClick}
        />
      </div>

      <div className='clearfix'/>
      <div className='report-footer'>
        <SecondaryButton
          name='column-option-save-button'
          size="regular"
          // disabled = {isSorting}
          disabled={isLock}
          onClick={processSave}>
          SAVE
        </SecondaryButton>
        <SecondaryButton
          name='column-option-cancel-button'
          size="regular"
          onClick={() => {setOrderBy({orderBy: '', orderByAscending: true}); onCancel();}}>
          CANCEL
        </SecondaryButton>
      </div>
    </div>
  );
}

export type SmartTableHandle = {
  showColumnSettings: VoidFunction,
};

type SmartTableProps = {
  tableName: string,
  overflowX?: string|undefined,
  overflowY?: string|undefined,
  columns: Array<TableColumn>,
  data: Array<any>,
  rowRenderers?: Array<TableRowRenderer>,
  width?: string | undefined,
  height?: string | undefined,
  /**
   * column settings is customizable or not
   */
  useStorage?: boolean,
  flexResizer?: boolean,
  customStorage?: {
    setItem: (storageKey:string, settings:any) => void,
    getItem: (storageKey:string) => Promise<any>,
  }|undefined,
  headersColor?: 'white' | 'gray' | 'purple' | 'olivegray',
  footerRow?: Array<any>|undefined,
  headerRow?: Array<any>|undefined,
  selectable?: boolean,
  alternateRows?: boolean,
  orderBy?: Array<TableOrderBy>|undefined,
  onHeaderClick?: ((x:TableColumn) => void)|undefined,
  customTableId?: string|undefined,
  getCustomCellProps?: any|undefined,
  rowProps?: any|undefined,
  tableStyle?: any|undefined,
  selectedRowIndex?: number,
  selectedColIndex?: number,
  tableVersion?: number|undefined,
  isListView?: boolean,
  useHighlightCellOnMouseover?:boolean
  
  useSetClickedCellWhenClick?:boolean,
  onInitialized?: VoidFunction,
  noHeaders?: boolean,
  renderBottomControls?: any,
  tableTitle?:string,
  cellRangeSelect?:boolean,
  useHeaderFilterParams?: {
    setData: (data: any[]) => void, 
    useHeaderFilter: boolean,
    oriListData: Array<any>, 
    disableBtnNameWhenFilterModalOpen: string,
    useLowerHeight? :  boolean,
  },
  useVirtual?:boolean,
  tableHelperWhiteSpace?: React.CSSProperties['whiteSpace'];
  isModalTable?:boolean,
  headersFontColor?: string |undefined,
  paging?:PagingModel,
  headerCheckVal?: boolean,
  //useTableHelperText?: boolean,
  useTableTextnoData?: boolean,
  rowAddActionRef?: React.MutableRefObject<'append' | 'insert' | null>;
};

const SmartTableImpl: React.ForwardRefRenderFunction<SmartTableHandle, SmartTableProps> = ({
  tableName,
  overflowX = undefined,
  overflowY = undefined,
  columns,
  data = [],
  rowRenderers = [],
  width = undefined,
  height = undefined,
  useStorage = false,
  flexResizer = false,
  customStorage=undefined,
  footerRow = undefined,
  headerRow = undefined,
  headersColor = 'white',
  selectable = false,
  alternateRows = false,
  orderBy = undefined,
  onHeaderClick = undefined,
  customTableId = undefined,
  getCustomCellProps = undefined,
  rowProps = undefined,
  tableStyle = undefined,
  selectedRowIndex,
  selectedColIndex,
  tableVersion = undefined,
  isListView = false,
  useHighlightCellOnMouseover = false,
  useSetClickedCellWhenClick= false,
  onInitialized = undefined,
  noHeaders = false,
  renderBottomControls,
  tableTitle,
  cellRangeSelect = true,
  useHeaderFilterParams = { setData: () => { }, useHeaderFilter: false, oriListData: [], disableBtnNameWhenFilterModalOpen: '', useLowerHeight : false },
  useVirtual = false,
  tableHelperWhiteSpace = 'normal',
  isModalTable = false,
  headersFontColor = undefined,
  headerCheckVal = false,
  paging = undefined,
  //useTableHelperText = false,
  useTableTextnoData = false,
  rowAddActionRef
}, ref) => {
  const appContext = useContext(ApplicationContext);
  const storageNameBase = `table-v-${(tableVersion ?? '')}-${storageVersion}-`;

  const storageKey = `${storageNameBase}${tableName}`;
  const storage = customStorage || localforage;
  const appliedTableId = customTableId ?? tableName;

  const [initialized, setInitialized] = useState(false);
  const [settings, setSettings] = useState<TableSettings>({columns: columns || []});
  const [showColumnConfiguration, setShowColumnConfiguration] = useState(false);
  
  function onCloumnOptionClose(){
    setShowColumnConfiguration(false);
  }

  React.useImperativeHandle(ref, () => ({
    showColumnSettings: () => {
      setShowColumnConfiguration(true);
    },
  }));

  useEffect(() => {
    if (initialized && onInitialized) {
      onInitialized();
    }
    return () => {
    }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialized]
  );

  const divRef = useCallback(node => {
    if (!node) {
      return; // Should never happen
    }

    if (useStorage) {
      initializeFromStorage(node.getBoundingClientRect().width);
    } else {
      intializeInMemory(node.getBoundingClientRect().width);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, tableName]);

  function intializeInMemory(containerWidth:number) {
    const settings = createDefaultSettings(overflowX, overflowY, columns, containerWidth);
   
    setSettings(settings);

    setInitialized(true);
  }

  async function initializeFromStorage(containerWidth: number) {
    let settingsFromStorage = await storage.getItem(storageKey);

    if (!settingsFromStorage) {
      if (storageKey.indexOf('tbl-list') > -1) {
        // storageKey does not exist, which mean new version or first access.
        //<pt250425: Merge new columns to previous columns version with new tableVersion-storageVersion.
        // find existing storageKey for the same tableName
        const locStorage = storage as LocalForage;
        const keys = await locStorage.keys();
        const key = keys.find((k) => k.substring(k.indexOf('tbl-list')) === tableName);
        let newColumns: TableColumn[] = [];
        let isCustomized = false;
        if (key) {
          // Previous version of columnSettings to check isCustomized and merge
          let tblSettings = await storage.getItem(key);

          // check isCustomized
          if (tblSettings.isCustomized === undefined) {
            tblSettings.isCustomized = checkIsCustomized2(tblSettings.columns);
          }

          if (tblSettings.isCustomized) {
            // check removed columns
            const oldColumns = tblSettings.columns as TableColumn[];
            let removed = oldColumns.filter((col) => _.findIndex(columns, (c) => c.accessor === col.accessor) < 0);
            let added = columns.filter((col) => _.findIndex(tblSettings.columns, (c: any) => c.accessor === col.accessor) < 0);

            oldColumns.forEach(c => {
              if (removed.indexOf(c) < 0)
                newColumns.push(c);
            });

            // update changes based on accessor
            newColumns.forEach((c: any) => {
              let newCol = columns.find(x => x.accessor === c.accessor);
              // Apply all prop except accessor, visible, ...
              _.forOwn(newCol, function (value, key) {
                if (['accessor', 'visible'].indexOf(key) < 0)
                  c[key] = value;
              });
            });
            added.forEach((col) => {
              const insertIndex = columns.findIndex((c) => c.accessor === col.accessor);
              newColumns.splice(insertIndex, 0, col);
            });
            // donot need to concern about col value which came from ExportCollection of ???Listitem

            isCustomized = true;
          }

          // remove existing key
          await locStorage.removeItem(key);
        }
        //pt250425>

        settingsFromStorage = createDefaultSettings(overflowX, overflowY, newColumns.length > 0 ? newColumns : columns, containerWidth);

        settingsFromStorage.isCustomized = isCustomized;
      }
      else {
        settingsFromStorage = createDefaultSettings(overflowX, overflowY, columns, containerWidth);
      }

      await storage.setItem(storageKey, settingsFromStorage);

    }
    else {
      if (storageKey.indexOf('tbl-list') > -1) {
        const storedCols = settingsFromStorage.columns as TableColumn[];
        const currentCols = [...storedCols];

        if (settingsFromStorage.isCustomized === undefined) {
          settingsFromStorage.isCustomized = checkIsCustomized(settingsFromStorage.columns);
        }

        // 외부 columns와 비교하여 추가/삭제만 반영
        const removed = currentCols.filter(
          (col) => _.findIndex(columns, (c) => c.accessor === col.accessor) < 0
        );
        const added = columns.filter(
          (col) => _.findIndex(currentCols, (c: any) => c.accessor === col.accessor) < 0
        );

        // 삭제된 컬럼 제거
        let newColumns = currentCols.filter((c) => removed.indexOf(c) < 0);

        // 새로 추가된 컬럼은 외부 columns 순서를 기준으로 정확히 그 자리에 삽입
        added.forEach((col) => {
          const insertIndex = columns.findIndex((c) => c.accessor === col.accessor);
          newColumns.splice(insertIndex, 0, col);
        });

        settingsFromStorage.columns = newColumns;

        await storage.setItem(storageKey, settingsFromStorage);
      }
    }

    if (storageKey.indexOf('tbl-list') > -1) {
      const tempCols = pinnedColumns(settingsFromStorage.columns);
      const changed = tempCols.some((c: TableColumn, i: number) =>
        c.accessor !== settingsFromStorage.columns[i]?.accessor || c.visible !== settingsFromStorage.columns[i]?.visible
      );

      settingsFromStorage.columns = tempCols;

      if (changed) {
        await storage.setItem(storageKey, settingsFromStorage);
      }
    }

    setSettings(settingsFromStorage);
    setInitialized(true);
  }

  /**
   * onSave handler for Column Settings popup
   * @param newColumns 
   */
  function saveColumnsSettings(newColumns: Array<TableColumn>) {
    // close popup
    setShowColumnConfiguration(false);

    const settingsCopy = _.cloneDeep(settings);
    settingsCopy.columns = newColumns;
    //<pt250428>
    settingsCopy.isCustomized = checkIsCustomized(newColumns);

    if (useStorage) {
     storage.setItem(storageKey, settingsCopy);
    }

    setSettings(settingsCopy);
  }

  /**
   * pt250428: Control settings.isCustomized by checing with default TableColumns[] in columns.
   * @param newColumns 
   * @returns 
   */
  function checkIsCustomized(newColumns: TableColumn[]) {
    let colChanged = false;
    let seqChanged = newColumns.some((d, i) => d.accessor !== columns[i].accessor);
    if (!seqChanged) {
      for (let i = 0; i < newColumns.length; i++) {
        let c = columns.find(x => x.accessor === newColumns[i].accessor);
        if (!c) return;  // Nerver happen
        if (c.visible !== newColumns[i].visible) {
          colChanged = true;
          break;
        }
      };
    }
    return seqChanged || colChanged;
  }

  function checkIsCustomized2(oldColumns: TableColumn[]) {
    let colChanged = false;
    let newColumns = columns.filter((col) => _.findIndex(oldColumns, (c:any) => c.accessor === col.accessor) >= 0);
    let oldColumns2 = oldColumns.filter((col) => _.findIndex(newColumns, (c) => c.accessor === col.accessor) >= 0);
    let seqChanged = oldColumns2.some((d, i) => d.accessor !== newColumns[i].accessor);
    if(!seqChanged) {
      for(let i = 0; i < oldColumns2.length; i++) {
        let c = newColumns.find(x => x.accessor === oldColumns2[i].accessor);
        if(!c) return;  // Nerver happen
        if(c.visible !== oldColumns2[i].visible) {
          colChanged = true;
          break;
        }
      };    
    }
    return seqChanged || colChanged;
  }

  function pinnedColumns(storedCols: TableColumn[]): TableColumn[] {
    // 저장된 배열에서 고정 컬럼을 제외한 나머지(사용자 설정)는 순서 그대로 유지 - 260710379 - 고정컬럼을 변경했을 경우 커스터마이징한 사용자는 고정컬럼이 먠 뒤로 가는 문제 발생
    const result = storedCols.filter(sc => {
      const def = columns.find(c => c.accessor === sc.accessor);
      return !def?.hiddenInColumnSetup;
    });

    // 고정 컬럼을 코드 정의 순서대로 삽입
    columns.forEach((def, idx) => {
      if (!def.hiddenInColumnSetup) return;

      const stored = storedCols.find(sc => sc.accessor === def.accessor);
      const col = stored ? { ...stored, visible: def.visible } : { ...def };
      result.splice(idx, 0, col);
    });
    return result;
  }

  function onColumnsResized(resizedColumns:Array<TableColumn>) {
    const settingsCopy = _.cloneDeep(settings);
    resizedColumns.forEach(r => {
      const actualIndex = settingsCopy.columns.findIndex(c => c.accessor === r.id); // Some columns could be hidden. Index in r param is not correct
      if (actualIndex >= 0) {
        settingsCopy.columns[actualIndex].width = r.width;
      }
    });

    // <chuck date="2025-07-30" desc="Save only the list view and not the table in the form" />
    //if (useStorage) {
    if (useStorage && storageKey.indexOf('tbl-list') > -1) {
     storage.setItem(storageKey, settingsCopy);
    }

    setSettings(settingsCopy);
  }

  function getActualIndex(visibleIndex:number) {
    if (visibleIndex < 0 || visibleIndex > settings.columns.length - 1) {
      return visibleIndex;
    }

    let visiblePassed = 0;
    let numOfInvisible = 0;

    for (let i = 0; i < settings.columns.length; ++i) {
      if (settings.columns[i].visible) {
        ++visiblePassed;
      } else {
        ++numOfInvisible
      }

      if (visiblePassed === visibleIndex) {
        break;
      }
    }

    return visibleIndex + numOfInvisible;
  }

  function onColumnReorder({from, to}:{from:number, to: number}) {
    if( to === 0 ) return; //fixed first column
    // Take into conideration possible hidden columns -> from, to parameters are indeces of visible columns
    let actualFrom = getActualIndex(from);
    let actualTo = getActualIndex(to);

    const hasStatusColumn = settings.columns.some(function (elem) {
      return elem?.accessor === 'status';
    });
    const hasRecogColumn = settings.columns.some(function (elem) {
      return elem?.accessor === 'recognized';
    });
    // const hasDIDColumn = settings.columns.some(function (elem) {
    //   return elem?.accessor === 'shipmentType';
    // });
    // const hasINVColumn = settings.columns.some(function (elem) {
    //   return elem?.accessor === 'invStat';
    // });
    // const hasBILColumn = settings.columns.some(function (elem) {
    //   return elem?.accessor === 'billStat';
    // });
    // const hasCrDbColumn = settings.columns.some(function (elem) {
    //   return elem?.accessor === 'crDbStat';
    // });  
    const hiddenColumnNum = settings.columns.filter(x => x.hiddenInColumnSetup && x.Header !== '').length;

    const canNotDropIndex = Number(hasStatusColumn) + Number(hasRecogColumn);
    // + Number(hasDIDColumn) + Number(hasINVColumn) + Number(hasBILColumn) + Number(hasCrDbColumn);

    const settingsCopy = _.cloneDeep(settings);

    if(actualTo < canNotDropIndex || actualTo < hiddenColumnNum) //can not drop lower than canNotDropIndex || hiddenColumnNum
      return;
    else
      settingsCopy.columns = arrayMoveImmutable(settingsCopy.columns, actualFrom, actualTo);

    if (useStorage) {
      storage.setItem(storageKey, settingsCopy);
    }

    setSettings(settingsCopy);
  }

  let displayColumns:Array<TableColumn> = [];

  if (initialized) {
    //<230616.dwshin. branch setting option에 따라서 column width 조정 되도록 수정>
    let enableBranch = (appContext?.systemSettings?.enableBranch ?? false);
    let useVat = (appContext?.systemSettings?.useVat ?? false);
    let enableIpRestr = (appContext?.systemSettings?.enableIpRestr ?? false);
    let colBranch = settings.columns.find(x => x.accessor === 'br' || x.accessor === 'branch');
    let colIpRestr = settings.columns.find(x => x.accessor === 'limitLoginIp' );

    let maxWidthAccessor = '';
    // option false 경우만 width 가공.
    //if ((!enableBranch && colBranch) || (!enableTrackRequest && colTrackingDate)) {
      if ((!enableBranch && colBranch) || (!enableIpRestr && colIpRestr)) {
      let colWidths = settings.columns.map(x => { return x.width ?? 0 });
      let colInitWidths = settings.columns.map(x => { return x.initialWidthPercentage ?? 0 });
      let max = Math.max(...colWidths, ...colInitWidths);
      if (colWidths && max > 0) {
        maxWidthAccessor = settings.columns.find(x => x.width === max || x.initialWidthPercentage === max)?.accessor ?? '';
      }
    }

    let tempColumns = settings.columns.filter(x => x.visible);
    if (!enableBranch) {
      tempColumns = tempColumns.filter(x => x.accessor !== 'br' && x.accessor !== 'branch');
    }
    if(!useVat){
      tempColumns = tempColumns.filter(x => x.accessor !== 'vatAmount' );
    }
    // if (!enableTrackRequest)
    //   tempColumns = tempColumns.filter(x => x.accessor !== 'trDate');
    if (!enableIpRestr) {
      tempColumns = tempColumns.filter(x => x.accessor !== 'limitLoginIp');
    }
    
    //displayColumns = settings.columns.filter(x => enableBranch ? x.visible : x.visible && x.accessor !== 'br' && x.accessor !== 'branch').map(x => {
    displayColumns = tempColumns.map(x => {
      const renderer = rowRenderers.find(r => r.accessor === x.accessor);
      if (renderer) {
        const result = { ...x };
        if (renderer.Cell) {
          result.Cell = renderer.Cell;
        }
        if (renderer.Header) {
          result.Header = renderer.Header;
        }

        if (maxWidthAccessor && result.accessor === maxWidthAccessor) {
          if (result.width)
            result.width += colBranch?.width ?? 0;

          if (result.initialWidthPercentage)
            result.initialWidthPercentage += colBranch?.initialWidthPercentage ?? 0;
        }
        
        result.headerFilterType = x.headerFilterType;

        return result;
      } else {
        return x;
      }
    });
  }

  return (
    <div ref={divRef}>
      {initialized &&
        <Table
          blackMode={appContext.useDarkMode}
          renderBottomControls={renderBottomControls}
          customTableId={appliedTableId}
          columns={displayColumns}
          data={data}
          height={height}
          width={width}
          overflowX={settings.overflowX}
          overflowY={settings.overflowY}
          onHeaderClick={onHeaderClick}
          onColumnReorder={onColumnReorder}
          onColumnsResized={onColumnsResized}
          flexResizer={flexResizer}
          footerRow={footerRow}
          headerRow={headerRow}
          headersColor={headersColor}
          selectable={selectable}
          alternateRows={alternateRows}
          orderBy={orderBy}
          getCustomCellProps={getCustomCellProps}
          rowProps={rowProps}
          tableStyle={tableStyle}
          selectedRowIndex={selectedRowIndex}
          selectedColIndex={selectedColIndex}
          isListView={isListView}
          useHighlightCellOnMouseover={useHighlightCellOnMouseover}
          useSetClickedCellWhenClick={useSetClickedCellWhenClick}
          noHeaders={noHeaders}
          tableTitle={tableTitle}
          cellRangeSelect={cellRangeSelect}
          useHeaderFilterParams={useHeaderFilterParams}
          useVirtual={useVirtual}
          tableHelperWhiteSpace={tableHelperWhiteSpace}
          isModalTable={isModalTable}
          headersFontColor={headersFontColor}
          headerCheckVal={headerCheckVal}
          paging={paging}
          //useTableHelperText={useTableHelperText}
          useTableTextnoData={useTableTextnoData}
          rowAddActionRef={rowAddActionRef}
        />
      }
      <IfmsModal isBlueHeader onCancel={onCloumnOptionClose} columnOptionCloseOnEsc draggable isOpen={showColumnConfiguration} title="Column Option" width="380px">
        {showColumnConfiguration &&
          <ColumnSettings
            originalColumns={columns}
            currentColumns={settings.columns}
            onCancel={() => setShowColumnConfiguration(false)}
            onSave={saveColumnsSettings}
          />
        }
      </IfmsModal>
    </div>
  );
};

const SmartTable = React.forwardRef(SmartTableImpl);

SmartTable.defaultProps = {
  columns: [],
  data: [],
  onHeaderClick: () => {},
  
  rowRenderers: [],
  useStorage: false,
  flexResizer: false,

  headersColor: 'white',
  selectable: false,
  alternateRows: false,
  selectedRowIndex: undefined,
  noHeaders: false,
  cellRangeSelect: true,
  isModalTable: false,
};

SmartTable.propTypes = {
  tableName: PropTypes.string.isRequired,
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  onHeaderClick: PropTypes.func.isRequired,
  overflowX: PropTypes.string,
  overflowY: PropTypes.string,
  rowRenderers: PropTypes.array.isRequired,
  height: PropTypes.string,
  width: PropTypes.string,
  useStorage: PropTypes.bool.isRequired,
  flexResizer: PropTypes.bool.isRequired,
  customStorage: PropTypes.shape({
    setItem: PropTypes.func.isRequired,
    getItem: PropTypes.func.isRequired,
  }),
  footerRow: PropTypes.array,
  headerRow: PropTypes.array,
  headersColor: PropTypes.oneOf(['white', 'gray', 'purple', 'olivegray']),
  selectable: PropTypes.bool.isRequired,
  alternateRows: PropTypes.bool.isRequired,
  orderBy: PropTypes.array,
  customTableId: PropTypes.string,
  getCustomCellProps: PropTypes.func,
  selectedRowIndex: PropTypes.number,
  selectedColIndex: PropTypes.number,
  onInitialized: PropTypes.func,
  noHeaders: PropTypes.bool.isRequired,
  cellRangeSelect: PropTypes.bool,
  isModalTable: PropTypes.bool.isRequired,
};

export default React.memo(SmartTable);

class ColSetting { //for headerSorting
  private static ExportCollection: XCol.XExportColumn[] = [ //for list sorting when header click
    XCol.Create({ col: 0, text: '', width: 10, dataIndex: 'columnName', type: XCol.XCellType.String }),
    XCol.Create({ col: 1, text: '', width: 10, dataIndex: 'visible', type: XCol.XCellType.Bool }),
  ];

  public static getExportCollection(): XCol.XExportColumn[] { //for list sorting when header click
    return this.ExportCollection;
  };
}
